<template>
  <div class="teacher-calendar navigation-top-app" :class="{'read-only': app.user.role === 'student'}">
    <navigation-top-app
      :app="app"
      :back="hasSideMenu && (filters.student === 'all' || app.user.role === 'student') ? null : () => {
        if (filters.student === 'all') {
          $router.push({name: 'app.teacher.home', query: {origin: 'app', navigationLeft: 'false', token: app.token}})
        } else {
          $router.push({name: 'app.student.profile', params: {code: filters.student}, query: {origin: 'app', navigationLeft: 'false', token: app.token}})
        }
      }"
      :right="isDesktop ? null : {
        icon: ['far', 'calendar-alt'],
        action: goToday
      }"
    >
      <template v-slot:center>
        <div id="navigation-center" v-if="isDesktop">
          {{ title }}
        </div>
        <div id="navigation-center" v-else-if="view === 'month'" v-on:click="toggleCalendar()">
          {{ title }}
          <font-awesome-icon class="icon" :icon="['fas', 'chevron-up']" v-if="calendar.open" />
          <font-awesome-icon class="icon" :icon="['fas', 'chevron-down']" v-if="!calendar.open" />
        </div>
        <div id="navigation-center" v-else>
          {{ title }}
        </div>
      </template>
    </navigation-top-app>
    <transition name="fade">
      <div id="teacher-calendar-content" class="max-width-800" :class="[fillsScreen ? 'week-view' : 'month-view', {'with-action': canBook}]" v-if="show">
        <div class="view-bar">
          <div class="view-switch">
            <button v-for="option in viewOptions" :key="option" :class="{active: view === option}" v-on:click="changeView(option)">{{ viewStrings[option] }}</button>
          </div>
          <!-- A phone's month moves with the arrows of its calendar. -->
          <div class="week-header" v-if="isDesktop || view !== 'month'">
            <div class="week-title">{{ viewTitle }}</div>
            <div class="week-controls">
              <button class="week-button" v-on:click="move(-1)"><font-awesome-icon :icon="['fas', 'chevron-left']" /></button>
              <button class="week-button today" v-if="isDesktop" v-on:click="move(0)">{{ app.strings.practice.calendar.app.week.today }}</button>
              <button class="week-button" v-on:click="move(1)"><font-awesome-icon :icon="['fas', 'chevron-right']" /></button>
            </div>
          </div>
        </div>
        <button class="next-practice" v-if="nextPractice && !practices.loading && practices.data.length === 0" v-on:click="openDay(nextPractice.day)">
          {{ app.strings.practice.calendar.app.next_class }} <b>{{ nextPractice.label }}</b>
          <font-awesome-icon :icon="['fas', 'chevron-right']" />
        </button>
        <div class="week-grid" :class="{loading: practices.loading}" v-if="isDesktop && view === 'week'">
          <div class="week-day" v-for="(day, dayIndex) in weekDays" :key="day.key" :class="{today: day.isToday, 'tip-left': dayIndex >= 5}">
            <div class="week-day-header">
              <span>{{ day.name }}</span>
              <b>{{ day.number }}</b>
            </div>
            <div class="week-day-body">
              <div class="week-practice" v-for="practice in day.practices" :key="practice.id" tabindex="0" :class="{ended: practice.ended === 1, cancelled: practice.cancelled === 1, clickable: app.user.role === 'teacher'}" v-on:click="openPractice(practice)" v-on:keyup.enter="openPractice(practice)">
                <!-- Full details on hover or keyboard focus: the card cuts long names. -->
                <div class="week-practice-tip" role="tooltip">
                  <div class="tip-time">{{ practice | startTime }} – {{ practice | endTime }} · {{ practice.time }}'</div>
                  <div class="tip-name">{{ app.user.role === 'teacher' ? practice.student_name : practice.teacher_name }}</div>
                  <div class="tip-meta">{{ practice.vehicle_name }} · {{ practice.license }}</div>
                  <div class="tip-meta" v-if="practice.meeting_point === 'other'"><font-awesome-icon :icon="['fas', 'map-marker-alt']" /> {{ practice.meeting_address }}</div>
                  <div class="tip-meta" v-if="practice.ended === 1 && practice.grade > 0">{{ app.strings.practice.start.grades[practice.grade] }}</div>
                  <div class="tip-meta" v-if="practice.cancelled === 1">{{ app.strings.practice.calendar.app.cancelled }}</div>
                  <div class="tip-meta" v-if="practice.incident">{{ incidentStrings.mark }}: {{ incidentStrings.types[practice.incident.type] }} · {{ practice.incident.minutes_given }} {{ incidentStrings.given }} · {{ incidentStrings.resolutions_short[practice.incident.resolution] }}</div>
                  <div class="tip-meta" v-else-if="practice.recovery_of">{{ incidentStrings.mark_recovery }}</div>
                </div>
                <div class="week-practice-time">{{ practice | startTime }}–{{ practice | endTime }}</div>
                <div class="week-practice-name" v-if="app.user.role === 'teacher'">{{ practice.student_name }}</div>
                <div class="week-practice-name" v-else>{{ practice.teacher_name }}</div>
                <div class="week-practice-meta">{{ practice.vehicle_name }} · {{ practice.license }}</div>
                <div class="week-practice-meeting" v-if="practice.meeting_point === 'other'" :title="practice.meeting_address">
                  <font-awesome-icon :icon="['fas', 'map-marker-alt']" /> {{ practice.meeting_address }}
                </div>
                <div class="practice-grade" :class="'grade-' + practice.grade" v-if="practice.ended === 1 && practice.grade > 0">
                  {{ app.strings.practice.start.grades[practice.grade] }}
                </div>
                <div class="practice-state" v-if="practice.cancelled === 1">{{ app.strings.practice.calendar.app.cancelled }}</div>
                <div class="practice-status" :class="practiceStatus(practice)" v-if="practiceStatus(practice)">{{ app.strings.practice.calendar.app.status[practiceStatus(practice)] }}</div>
                <div class="practice-mark" v-if="practice.incident">{{ incidentStrings.mark }} · {{ incidentStrings.resolutions_short[practice.incident.resolution] }}</div>
                <div class="practice-mark recovery" v-else-if="practice.recovery_of">{{ incidentStrings.mark_recovery }}</div>
              </div>
              <div class="week-empty" v-if="!practices.loading && day.practices.length === 0">{{ app.strings.practice.calendar.app.week.empty }}</div>
            </div>
          </div>
        </div>
        <div class="month-grid" :class="{loading: practices.loading}" v-if="isDesktop && view === 'month'">
          <div class="month-weekday" v-for="name in monthWeekdays" :key="name">{{ name }}</div>
          <div class="month-cell" v-for="cell in monthCells" :key="cell.key" :class="{today: cell.isToday, other: !cell.inMonth}" v-on:click="openDay(cell.date)">
            <div class="month-cell-number">{{ cell.number }}</div>
            <div class="month-practice" v-for="practice in cell.practices.slice(0, 3)" :key="practice.id" :class="{ended: practice.ended === 1, cancelled: practice.cancelled === 1}" v-on:click.stop="openMonthPractice(practice, cell.date)">
              <b>{{ practice | startTime }}</b>
              <span>{{ app.user.role === 'teacher' ? practice.student_name : practice.teacher_name }}</span>
            </div>
            <div class="month-more" v-if="cell.practices.length > 3">{{ app.strings.app.teacher.home.next.more.replace('{n}', cell.practices.length - 3) }}</div>
          </div>
        </div>
        <div class="calendar-slot" v-if="!isDesktop && view === 'month'">
          <calendar-small
            id="calendar"
            ref="calendar"
            :app="app"
            :collapsed="!calendar.open"
            :hilighted="selected"
            :marks="marks"
            @onDayClick="daySelected"
            @onMonthMove="monthMoved"
            @onYearMove="(year) => {
              this.calendar.year = year
            }"
            @onMove="() => {
              this.practiceReadAll()
            }"
          ></calendar-small>
        </div>
        <div class="practice-list" v-if="!fillsScreen">
          <div ref="list">
            <transition-group name="fade">
              <template v-if="!practices.loading">
                <template v-for="(practices, day) in practicesDays">
                  <div class="practice-list-day" :class="'day-' + day.split(' ')[1]" v-bind:key="day" v-if="view !== 'day'">{{ day }}</div>
                  <template v-for="(practice, i) in practices">
                    <div class="practice-data" :class="{ended: practice.ended === 1, cancelled: practice.cancelled === 1}" v-bind:key="day + '-' + i" v-on:click="openPractice(practice)">
                      <div>
                        <div>
                          <div>
                            {{ practice | startTime }}
                          </div>
                          <div></div>
                          <div>
                            {{ practice | endTime }}
                          </div>
                        </div>
                        <div>
                          {{ practice.time }}'
                        </div>
                      </div>
                      <div>
                        <div v-if="app.user.role === 'teacher'">
                          {{ practice.student_name }}
                        </div>
                        <div v-if="app.user.role === 'student'">
                          {{ practice.teacher_name }}
                        </div>
                        <div>
                          {{ practice.vehicle_name }}
                        </div>
                        <div>
                          {{ practice.license }}
                        </div>
                        <div class="practice-meeting" v-if="practice.meeting_point === 'other'">
                          <font-awesome-icon :icon="['fas', 'map-marker-alt']" /> {{ practice.meeting_address }}
                        </div>
                        <div class="practice-grade" :class="'grade-' + practice.grade" v-if="practice.ended === 1 && practice.grade > 0">
                          {{ app.strings.practice.start.grades[practice.grade] }}
                        </div>
                        <div class="practice-state" v-if="practice.cancelled === 1">{{ app.strings.practice.calendar.app.cancelled }}</div>
                        <div class="practice-status" :class="practiceStatus(practice)" v-if="practiceStatus(practice)">{{ app.strings.practice.calendar.app.status[practiceStatus(practice)] }}</div>
                        <div class="practice-mark" v-if="practice.incident">{{ incidentStrings.mark }} · {{ incidentStrings.resolutions_short[practice.incident.resolution] }}</div>
                        <div class="practice-mark recovery" v-else-if="practice.recovery_of">{{ incidentStrings.mark_recovery }}</div>
                      </div>
                    </div>
                  </template>
                </template>
              </template>
            </transition-group>
            <div class="practice-empty" v-if="!practices.loading && practices.data.length === 0">
              {{ view === 'month' ? app.strings.practice.calendar.app.month_empty : app.strings.practice.calendar.app.week.empty }}
            </div>
            <transition name="fade">
              <template v-if="practices.loading">
                <loader-dot :size="'16px'" :color_1="'rgb(32, 113, 183)'" :color_2="'rgb(32, 113, 183)'" />
              </template>
            </transition>
          </div>
        </div>
        <div id="practice-add" v-on:click="$router.push({name: 'app.teacher.practice.create', query: createQuery})" v-if="app.user.role === 'teacher'">
          <font-awesome-icon class="icon" :icon="['fas', 'plus']"/>
        </div>
      </div>
    </transition>
    <div class="bottom-action" v-if="canBook">
      <button class="h-button w-100" v-on:click="$router.push({name: 'app.student.practice.book', query: {origin: 'app', navigationLeft: 'false', token: app.token}})">
        {{ app.strings.app.student.practice.book.menu }}
      </button>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'app-practice-calendar-view',
  components: {
  },
  data () {
    return {
      show: false,
      today: {
        day: new Date().getDate(),
        month: new Date().getMonth(),
        year: new Date().getFullYear()
      },
      // The day marked on the month. Day 0: none, after moving to another month.
      selected: {
        day: new Date().getDate(),
        month: new Date().getMonth(),
        year: new Date().getFullYear()
      },
      calendar: {
        open: true,
        month: 0,
        year: 0
      },
      practices: {
        loading: false,
        data: []
      },
      // Only the last request counts: a slow answer must not paint another month.
      requestId: 0,
      // Day to scroll to when the month being read arrives.
      pendingDay: null,
      filters: {
        student: 'all'
      },
      // From tablet up (>= 768px) the seven days fit, so the week fills the
      // screen; a phone keeps the month list.
      isDesktop: window.matchMedia('(min-width: 768px)').matches,
      // Desktop (>= 1024px): the side menu already leads back.
      hasSideMenu: window.matchMedia('(min-width: 1024px)').matches,
      // What is on screen: the day, the week or the month holding anchor.
      view: window.matchMedia('(min-width: 768px)').matches ? 'week' : 'month',
      viewOptions: ['day', 'week', 'month'],
      anchor: moment(),
      // A student's next class, to point at it from an empty day or week.
      nextPractice: null
    }
  },
  computed: {
    // The school lets the student book from the app (direct or request).
    canBook () {
      return this.app.user.role === 'student' && !!this.app.studentBooking && this.app.studentBooking !== 'off'
    },
    title () {
      if (this.filters.student === 'all') {
        return this.app.strings.practice.calendar.app.title_teacher
      }
      return this.app.user.role === 'teacher' ? this.app.strings.practice.calendar.app.title_of_student : this.app.strings.practice.calendar.app.title_student
    },
    // From the agenda of a student, a new class is for that student.
    createQuery () {
      let query = {origin: 'app', navigationLeft: 'false', token: this.app.token}
      if (this.filters.student !== 'all') {
        query.student = this.filters.student
      }
      return query
    },
    incidentStrings () {
      return this.app.strings.components.practice.incident
    },
    practicesDays () {
      let days = {}

      for (let i in this.practices.data) {
        let practice = this.practices.data[i]
        let day = moment(new Date(practice.date)).locale('es').format('dddd DD')

        if (!(day in days)) {
          days[day] = []
        }

        days[day].push(practice)
      }

      return days
    },
    viewStrings () {
      return this.app.strings.practice.calendar.app.views
    },
    // These fill the screen by themselves; the rest are a list that scrolls.
    fillsScreen () {
      return this.isDesktop && this.view !== 'day'
    },
    weekStart () {
      return moment(this.anchor).startOf('isoWeek')
    },
    viewTitle () {
      if (this.view === 'day') {
        return moment(this.anchor).locale('es').format('dddd, D MMM')
      }
      if (this.view === 'month') {
        return moment(this.anchor).locale('es').format('MMMM YYYY')
      }
      return this.weekTitle
    },
    monthWeekdays () {
      let names = []
      for (let i = 0; i < 7; i++) {
        names.push(moment(this.weekStart).add(i, 'days').locale('es').format('ddd'))
      }
      return names
    },
    monthCells () {
      let cells = []
      let today = moment().format('YYYY-MM-DD')
      let day = moment(this.anchor).startOf('month').startOf('isoWeek')
      let last = moment(this.anchor).endOf('month').endOf('isoWeek')
      while (day.isSameOrBefore(last, 'day')) {
        let key = day.format('YYYY-MM-DD')
        cells.push({
          key: key,
          date: moment(day),
          number: day.format('D'),
          isToday: key === today,
          inMonth: day.month() === this.anchor.month(),
          practices: this.practices.data.filter((p) => moment(new Date(p.date)).format('YYYY-MM-DD') === key)
        })
        day.add(1, 'days')
      }
      return cells
    },
    weekDays () {
      let days = []
      let today = moment().format('YYYY-MM-DD')
      for (let i = 0; i < 7; i++) {
        let day = moment(this.weekStart).add(i, 'days')
        let key = day.format('YYYY-MM-DD')
        days.push({
          key: key,
          name: day.locale('es').format('ddd'),
          number: day.format('D'),
          isToday: key === today,
          practices: this.practices.data.filter((p) => moment(new Date(p.date)).format('YYYY-MM-DD') === key)
        })
      }
      return days
    },
    weekTitle () {
      let end = moment(this.weekStart).add(6, 'days')
      return this.weekStart.locale('es').format('D MMM') + ' – ' + end.locale('es').format('D MMM YYYY')
    },
    marks () {
      let marks = []

      for (let i in this.practices.data) {
        let practice = this.practices.data[i]
        let date = new Date(practice.date)

        marks.push(date.getDate())
      }

      return marks
    }
  },
  props: [
    'app'
  ],
  filters: {
    startTime (practice) {
      let hour = (practice.hour < 10 ? '0' : '') + practice.hour
      let minute = (practice.minute < 10 ? '0' : '') + practice.minute

      return hour + ':' + minute
    },
    endTime (practice) {
      let hour = practice.hour
      let minute = practice.minute + practice.time

      while (minute >= 60) {
        hour++
        minute -= 60

        if (hour > 23) {
          hour = 0
        }
      }

      hour = (hour < 10 ? '0' : '') + hour
      minute = (minute < 10 ? '0' : '') + minute

      return hour + ':' + minute
    }
  },
  mounted () {
    let student = this.$route.query.student

    if (student === undefined || student === null) {
      student = 'all'
    }

    this.filters.student = student

    this.syncCalendar()

    this.media = window.matchMedia('(min-width: 768px)')
    this.onMedia = () => {
      this.isDesktop = this.media.matches
      this.practiceReadAll()
    }
    this.media.addListener(this.onMedia)
    this.sideMenuMedia = window.matchMedia('(min-width: 1024px)')
    this.onSideMenuMedia = () => {
      this.hasSideMenu = this.sideMenuMedia.matches
    }
    this.sideMenuMedia.addListener(this.onSideMenuMedia)

    this.practiceReadAll()

    this.show = true

    // The session user may still be loading when the view mounts.
    this.stopRoleWatch = this.$watch(() => this.app.user.role, (role) => {
      if (role === 'student') {
        this.loadStudentBooking(this.app)
        this.nextPracticeRead()
      }
    }, {immediate: true})
  },
  beforeDestroy () {
    this.stopRoleWatch()
    if (this.media) {
      this.media.removeListener(this.onMedia)
      this.sideMenuMedia.removeListener(this.onSideMenuMedia)
    }
  },
  methods: {
    // direction 0: back to today.
    move (direction) {
      this.anchor = direction === 0 ? moment() : moment(this.anchor).add(direction, this.view)
      this.syncCalendar()
      this.practiceReadAll()
    },
    changeView (view) {
      this.view = view
      this.syncCalendar()
      this.practiceReadAll()
    },
    openDay (date) {
      this.anchor = moment(date)
      this.changeView('day')
    },
    syncCalendar () {
      this.calendar.month = this.anchor.month()
      this.calendar.year = this.anchor.year()
      this.selected = {day: this.anchor.date(), month: this.anchor.month(), year: this.anchor.year()}
    },
    toggleCalendar () {
      this.calendar.open = !this.calendar.open
    },
    daySelected (day) {
      this.selected = {day: day, month: this.calendar.month, year: this.calendar.year}
      this.anchor = moment({year: this.calendar.year, month: this.calendar.month, day: day})
      this.scrollToDay(day)
    },
    monthMoved (month) {
      this.calendar.month = month
      this.selected = {day: 0, month: month, year: this.calendar.year}
      this.anchor = moment({year: this.calendar.year, month: month, day: 1})
    },
    goToday () {
      if (this.view !== 'month') {
        this.move(0)
        return
      }
      this.calendar.open = true
      if (this.calendar.month === this.today.month && this.calendar.year === this.today.year) {
        this.daySelected(this.today.day)
        return
      }
      this.pendingDay = this.today.day
      this.$refs.calendar.moveTo(this.today.month, this.today.year)
      this.selected = {day: this.today.day, month: this.today.month, year: this.today.year}
      this.anchor = moment()
    },
    practiceReadAll () {
      const request = ++this.requestId

      this.practices.loading = true
      this.practices.data = []

      let start = moment(this.anchor).startOf(this.view === 'week' ? 'isoWeek' : this.view)
      let end = moment(start).endOf(this.view === 'week' ? 'isoWeek' : this.view)
      // The month grid also shows the ends of the weeks around it.
      if (this.view === 'month' && this.isDesktop) {
        start.startOf('isoWeek')
        end.endOf('isoWeek')
      }
      let startDate = start.format('YYYY-MM-DD')
      let endDate = end.format('YYYY-MM-DD')

      let options = {
        start_date: startDate,
        end_date: endDate,
        student: this.filters.student
      }

      if (this.app.user.role === 'teacher') {
        options.teacher = this.app.user.code
      }

      this.$http.post(
        this.app.url.api + this.app.endpoints.practice.filter,
        options,
        {
          emulateJSON: true,
          headers: {
            Authorization: 'Token ' + this.app.token
          }
        }
      ).then(response => {
        if (request !== this.requestId) {
          return
        }
        if (response.status === 200) {
          if (response.body.success === true) {
            this.practices.data = response.body.data.practices
          } else {
            if (response.body.error.code === 'S100') {
              this.app.methods.logout()
            } else {
              this.showRequestError(this.app.strings, 'practice-filter', response.body.error)
            }
          }
        } else {
          this.showError(this.app.strings, 'request', 'status-' + response.status)
        }
        this.practices.loading = false
        if (this.pendingDay !== null) {
          const day = this.pendingDay
          this.pendingDay = null
          this.$nextTick(() => this.scrollToDay(day))
        }
      }, () => {
        if (request !== this.requestId) {
          return
        }
        this.practices.loading = false
        this.showError(this.app.strings, 'request', 'status-network: Connection unreachable')
      })
    },
    practiceStart (practice) {
      let date = new Date(practice.date)
      return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), parseInt(practice.hour), parseInt(practice.minute), 0, 0)
    },
    // Only for a student: a teacher already sees it on the class menu.
    practiceStatus (practice) {
      if (this.app.user.role !== 'student' || practice.cancelled === 1) {
        return ''
      }
      if (practice.ended === 1) {
        return 'ended'
      }
      let end = this.practiceStart(practice).getTime() + parseInt(practice.time) * 60000
      return end < Date.now() ? 'unfinished' : ''
    },
    nextPracticeRead () {
      this.$http.post(
        this.app.url.api + this.app.endpoints.practice.filter,
        {
          start_date: moment().format('YYYY-MM-DD'),
          end_date: moment().add(1, 'year').format('YYYY-MM-DD'),
          student: this.filters.student
        },
        {
          emulateJSON: true,
          headers: {
            Authorization: 'Token ' + this.app.token
          }
        }
      ).then(response => {
        if (response.status !== 200 || response.body.success !== true) {
          return
        }
        let now = Date.now()
        let next = response.body.data.practices
          .filter((p) => p.cancelled !== 1 && p.ended !== 1 && this.practiceStart(p).getTime() + parseInt(p.time) * 60000 > now)
          .sort((a, b) => this.practiceStart(a) - this.practiceStart(b))[0]
        if (next) {
          let start = moment(this.practiceStart(next))
          this.nextPractice = {
            day: start,
            label: start.locale('es').format('dddd D [de] MMMM, HH:mm')
          }
        }
      }, () => {})
    },
    scrollToDay (day) {
      // The list writes its days with two digits.
      const el = this.$el.getElementsByClassName('day-' + (day < 10 ? '0' : '') + day)[0]
      const list = this.$refs.list

      if (el && list) {
        list.scrollTo({top: el.offsetTop - 8, behavior: 'smooth'})
      }
    },
    // Only a teacher has a class screen; a student goes to that day.
    openMonthPractice (practice, date) {
      if (this.app.user.role === 'teacher') {
        this.openPractice(practice)
      } else {
        this.openDay(date)
      }
    },
    openPractice (practice) {
      if (this.app.user.role === 'teacher') {
        this.$router.push({name: 'app.teacher.practice.menu', params: {id: practice.id}, query: {origin: 'app', navigationLeft: 'false', token: this.app.token}})
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/variables-app.scss";
@import "@/assets/styles/variables-valkiria.scss";
@import "@/assets/styles/mobile-app.scss";

.teacher-calendar {
  display: flex;
  flex-direction: column;
  padding: 0 !important;
}

#navigation-center > .icon {
  font-size: 10px;
  margin-left: 6px;
}

#teacher-calendar-content {
  position: relative;
  width: 100%;
  height: 100%;
  padding-top: 60px;
  box-sizing: border-box;
  margin: auto;
}

// Phone: the month on top and the list taking the rest, each with its own
// scroll.
#teacher-calendar-content.month-view {
  display: flex;
  flex-direction: column;
}

.view-bar {
  flex: none;
  padding: 12px 12px 0;
}

.view-switch {
  display: flex;
  padding: 3px;
  border-radius: 12px;
  background-color: rgba(100, 116, 139, 0.12);
}

.view-switch > button {
  flex: 1;
  height: 36px;
  border: 0;
  border-radius: 9px;
  background-color: transparent;
  color: #475569;
  font-family: 'Lexend', sans-serif;
  font-size: 14px;
  font-weight: 600;
}

.view-switch > button.active {
  background-color: #FFFFFF;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.16);
  color: #2071b7;
}

.month-grid {
  display: grid;
  flex: 1;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  grid-auto-rows: minmax(96px, 1fr);
  grid-template-rows: auto;
  gap: 6px;
  padding-bottom: 96px;
  transition: opacity 0.2s ease;
}

.month-grid.loading {
  opacity: 0.5;
}

.month-weekday {
  color: #64748b;
  font-family: 'Lexend', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-align: center;
  text-transform: uppercase;
}

.month-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  padding: 6px;
  box-sizing: border-box;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  background-color: #FFFFFF;
  cursor: pointer;
}

.month-cell.other {
  background-color: transparent;
  opacity: 0.55;
}

.month-cell.today {
  border-color: #2071b7;
}

.month-cell-number {
  color: #1f2a37;
  font-family: 'Lexend', sans-serif;
  font-size: 14px;
  font-weight: 700;
}

.month-cell.today .month-cell-number {
  color: #2071b7;
}

.month-practice {
  display: flex;
  gap: 4px;
  min-width: 0;
  padding: 2px 4px;
  border-left: 3px solid #2071b7;
  border-radius: 4px;
  background-color: #f5f8fb;
  color: #1f2a37;
  font-size: 11px;
  line-height: 1.3;
}

.month-practice.ended {
  border-left-color: #16a34a;
}

.month-practice.cancelled {
  border-left-color: #dc3545;
  opacity: 0.7;
}

.month-practice > b {
  flex: none;
  color: #2071b7;
}

.month-practice > span {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.month-more {
  color: #64748b;
  font-size: 11px;
}

.calendar-slot {
  flex: none;
  padding: 12px 12px 0;
  box-sizing: border-box;
}

.practice-list {
  flex: 1;
  min-height: 0;
  padding: 0 12px;
  box-sizing: border-box;
  width: 100%;
}

// The bottom space keeps the + button off the last class.
.practice-list > div {
  position: relative;
  width: 100%;
  height: 100%;
  padding-bottom: 96px;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
}

.practice-list-day {
  margin: 18px 4px 8px;
  color: #64748b;
  font-family: 'Lexend', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.practice-data {
  display: flex;
  margin: 10px 0;
  min-height: 96px;
  padding: 12px;
  box-sizing: border-box;
  background-color: #FFFFFF;
  border: 1px solid #e8edf3;
  border-left: 5px solid #2071b7;
  border-radius: 20px;
  box-shadow: 0 12px 30px rgba(20, 67, 107, 0.08);
  cursor: pointer;
}

.practice-data.ended {
  border-left-color: #16a34a;
}

.practice-data.cancelled {
  border-left-color: #dc3545;
  opacity: 0.7;
}

.practice-data > div:nth-of-type(1) {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 76px;
  align-items: center;
  justify-content: center;
}

.practice-data > div:nth-of-type(1) > div:nth-of-type(1) {
  display: flex;
  flex-direction: column;
  width: 64px;
  padding: 8px 0;
  border-radius: 14px;
  background-color: rgba(32, 113, 183, 0.1);
  align-items: center;
  justify-content: center;
  color: #2071b7;
  font-family: 'Lexend', sans-serif;
  font-size: 15px;
  font-weight: 600;
}

.practice-data.ended > div:nth-of-type(1) > div:nth-of-type(1) {
  background-color: rgba(22, 163, 74, 0.12);
  color: #16a34a;
}

.practice-data.cancelled > div:nth-of-type(1) > div:nth-of-type(1) {
  background-color: rgba(220, 53, 69, 0.12);
  color: #dc3545;
}

.practice-data > div:nth-of-type(1) > div:nth-of-type(1) > div:nth-of-type(2) {
  width: 60%;
  height: 1px;
  background-color: currentColor;
  opacity: 0.35;
  margin: 3px 0;
}

.practice-data > div:nth-of-type(1) > div:nth-of-type(2) {
  margin-top: 6px;
  color: #64748b;
  font-family: 'Lexend', sans-serif;
  font-size: 11px;
  font-weight: 700;
}

.practice-data > div:nth-of-type(2) {
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: center;
  padding-left: 12px;
}

.practice-data > div:nth-of-type(2) > div:nth-of-type(1) {
  color: #1f2a37;
  font-family: 'Lexend', sans-serif;
  font-size: 16px;
  font-weight: 600;
  padding: 2px 0;
}

.practice-data > div:nth-of-type(2) > div:nth-of-type(2) {
  color: #64748b;
  font-size: 14px;
  padding: 2px 0;
}

.practice-data > div:nth-of-type(2) > div:nth-of-type(3) {
  color: #64748b;
  font-size: 12px;
  padding: 2px 0;
}

#practice-add {
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  bottom: 28px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background-color: #2071b7;
  box-shadow: 0 10px 20px rgba(32, 113, 183, 0.32);
  color: #FFFFFF;
  font-size: 20px;
  z-index: 600;
}

.practice-meeting {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  color: #2071b7 !important;
  font-size: 13px !important;
}

.practice-grade {
  align-self: flex-start;
  margin-top: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-family: 'Lexend', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.practice-grade.grade-1 { background-color: rgba(220, 53, 69, 0.12); color: #b91c1c; }
.practice-grade.grade-2 { background-color: #fff4e6; color: #d97706; }
.practice-grade.grade-3 { background-color: rgba(32, 113, 183, 0.1); color: #2071b7; }
.practice-grade.grade-4 { background-color: rgba(22, 163, 74, 0.12); color: #0f766e; }

#teacher-calendar-content.with-action {
  padding-bottom: 82px !important;
}

// A student's classes are information: they don't open anything.
.read-only .practice-data,
.read-only .week-practice {
  cursor: default !important;
  filter: none !important;
  scale: 1 !important;
}

.next-practice {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  flex: none;
  width: calc(100% - 24px);
  margin: 10px 12px 0;
  padding: 12px 14px;
  box-sizing: border-box;
  border: none;
  border-radius: 14px;
  background-color: #eaf3fb;
  color: #1a5d98;
  font-size: 14px;
  text-align: left;
}

.next-practice > b::first-letter {
  text-transform: uppercase;
}

.next-practice > svg {
  margin-left: auto;
  font-size: 12px;
}

.practice-status {
  align-self: flex-start;
  width: fit-content;
  margin-top: 4px;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

.practice-status.ended {
  background-color: rgba(22, 163, 74, 0.12);
  color: #15803d;
}

.practice-status.unfinished {
  background-color: #fff4e6;
  color: #b45309;
}

.practice-empty {
  margin: 24px 4px;
  padding: 28px 20px;
  border: 1px solid #e8edf3;
  border-radius: 20px;
  background-color: #FFFFFF;
  color: #64748b;
  text-align: center;
}

.practice-mark {
  align-self: flex-start;
  margin-top: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background-color: #fff4e6;
  color: #b45309;
  font-family: 'Lexend', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.practice-mark.recovery {
  background-color: rgba(124, 58, 237, 0.1);
  color: #6d28d9;
}

.practice-state {
  align-self: flex-start;
  margin-top: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background-color: rgba(220, 53, 69, 0.12);
  color: #b91c1c;
  font-family: 'Lexend', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

// ------------------------------------------------------------ desktop week
.week-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0 10px;
}

.week-title {
  color: #0f172a;
  font-family: 'Lexend', sans-serif;
  font-size: 17px;
  font-weight: 700;
}

.week-title::first-letter {
  text-transform: uppercase;
}

// A day and its classes are few: not stretched across a whole wide screen.
@media (min-width: 768px) {
  .week-title {
    font-size: 22px;
  }

  .view-bar {
    padding: 16px 0 0;
  }

  .view-switch {
    width: 300px;
  }

  #teacher-calendar-content.month-view .practice-list {
    width: 100%;
    max-width: 720px;
    padding: 0;
  }
}

.week-controls {
  display: flex;
  gap: 8px;
}

.week-button {
  min-width: 42px;
  height: 42px;
  padding: 0 14px;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  background-color: #FFFFFF;
  color: #334155;
  font-family: 'Lexend', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.week-button:hover {
  border-color: #2071b7;
  color: #2071b7;
}

.week-button.today {
  background-color: #2071b7;
  border-color: #2071b7;
  color: #FFFFFF;
}

.week-grid {
  display: grid;
  flex: 1;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 12px;
  padding-bottom: 96px;
  transition: opacity 0.2s ease;
}

// The days reach the bottom of the screen, whatever the week holds.
#teacher-calendar-content.week-view {
  display: flex;
  flex-direction: column;
  height: auto;
  min-height: 100%;
}

@media (min-width: 768px) and (max-width: 1023px) {
  #teacher-calendar-content.week-view,
  #teacher-calendar-content.month-view {
    padding-left: 16px;
    padding-right: 16px;
  }

  .week-grid {
    gap: 8px;
  }

  .week-day-header {
    padding: 10px 8px !important;
  }

  .week-day-body {
    padding: 6px !important;
  }

  .week-practice {
    padding: 8px !important;
  }

  .week-practice-meta {
    font-size: 11px !important;
  }
}

.week-grid.loading {
  opacity: 0.5;
}

.week-day {
  display: flex;
  flex-direction: column;
  min-height: 420px;
  border: 1px solid #e8edf3;
  border-radius: 18px;
  background-color: #FFFFFF;
  box-shadow: 0 12px 30px rgba(20, 67, 107, 0.06);
}

.week-day.today {
  border-color: rgba(32, 113, 183, 0.55);
}

.week-day-header {
  display: flex;
  border-radius: 18px 18px 0 0;
  align-items: baseline;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #e8edf3;
  color: #64748b;
  font-family: 'Lexend', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.week-day-header > b {
  color: #1f2a37;
  font-size: 20px;
  letter-spacing: 0;
}

.week-day.today .week-day-header {
  background-color: rgba(32, 113, 183, 0.08);
  color: #2071b7;
}

.week-day.today .week-day-header > b {
  color: #2071b7;
}

.week-day-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
}

.week-practice {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 10px;
  border: 1px solid #e8edf3;
  border-left: 4px solid #2071b7;
  border-radius: 12px;
  background-color: #f5f8fb;
}

.week-practice.clickable {
  cursor: pointer;
}

.week-practice.clickable:hover {
  background-color: rgba(32, 113, 183, 0.08);
}

.week-practice.ended {
  border-left-color: #16a34a;
}

.week-practice.cancelled {
  border-left-color: #dc3545;
  opacity: 0.7;
}

.week-practice-time {
  color: #2071b7;
  font-family: 'Lexend', sans-serif;
  font-size: 12px;
  font-weight: 700;
}

// From here the hours fit on one line of the seven columns.
@media (min-width: 1200px) {
  .week-practice-time {
    white-space: nowrap;
  }
}

.week-practice.ended .week-practice-time {
  color: #16a34a;
}

.week-practice:focus {
  outline: 2px solid rgba(32, 113, 183, 0.45);
  outline-offset: 2px;
}

.week-practice-tip {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 30;
  display: none;
  width: max-content;
  min-width: 100%;
  max-width: 280px;
  padding: 12px 14px;
  border-radius: 12px;
  background-color: #0f172a;
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.28);
  color: #FFFFFF;
  font-size: 13px;
  line-height: 1.4;
  white-space: normal;
  pointer-events: none;
}

.week-day.tip-left .week-practice-tip {
  right: 0;
  left: auto;
}

.week-practice:hover,
.week-practice:focus {
  z-index: 40;
}

.week-practice:hover .week-practice-tip,
.week-practice:focus .week-practice-tip {
  display: block;
}

.tip-time {
  color: #93c5fd;
  font-family: 'Lexend', sans-serif;
  font-size: 12px;
  font-weight: 700;
}

.tip-name {
  margin: 2px 0 4px;
  font-family: 'Lexend', sans-serif;
  font-size: 15px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.tip-meta {
  color: #cbd5e1;
  overflow-wrap: anywhere;
}

.week-practice-name {
  display: -webkit-box;
  overflow: hidden;
  margin-top: 4px;
  color: #1f2a37;
  font-family: 'Lexend', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.week-practice-meta {
  color: #64748b;
  font-size: 12px;
  overflow-wrap: anywhere;
}

.week-practice-meeting {
  margin-top: 4px;
  color: #2071b7;
  font-size: 12px;
  overflow-wrap: anywhere;
}

.week-empty {
  padding: 18px 4px;
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
}

@media (min-width: 1024px) {
  #practice-add {
    bottom: 32px;
    right: 32px;
  }
}
</style>
